import React, { useContext, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { LanguageContext } from "@lib/i18n/LanguageProvider";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";

import { CustomForm } from "albinasoft-ui-package";
import { ElementType } from "albinasoft-ui-package/dist/components/CustomForm";
import { ClientTrackRequest, ClientTrackResponse } from "@models/ClientTrackModels";
import ClientTrackApi from "api/ClientTrackApi";
import { GlobalOperations } from "config/GlobalApiOperations";

const onNotify = {
  success: (m: string) => toast.success(m),
  error: (m: string) => toast.error(m),
  warning: (m: string) => toast.warning(m),
};

// Layout context types
type LayoutContextType = {
  setLayoutContext: (title: string) => void;
  setLayoutHeaderImage: (url: string) => void;
  setPageTitle: (pageTitle: string) => void;
  setPageSummary: (pageSummary: string) => void;
  setVideoHelpUrl: (helpUrl: string) => void;
  setShowHeader: (showHeader: boolean) => void;
  setShowSidebar: (showSidebar: boolean) => void;
  setShowSummaryOnMobile: (showSummaryOnMobile: boolean) => void;
};

// API’den gelecek yapı
interface PageConfig {
  rawFormElements: Array<Record<string, any>>;
  initialValues: Record<string, any>;
  confirmLabel: string;
  showCancelButton: boolean;
  cancelLabel: string;
}

const RequestPage: React.FC = () => {
  const {
    setPageTitle,
    setPageSummary,
    setVideoHelpUrl,
    setShowHeader,
    setShowSidebar,
    setShowSummaryOnMobile,
    setLayoutHeaderImage,
  } = useOutletContext<LayoutContextType>();
  const { translations = {} } = useContext(LanguageContext) || {};

  // ———————— Hooks hep en üstte ————————
  const [config, setConfig] = useState<PageConfig | null>(null);
  const [pendingFileCounts, setPendingFileCounts] = useState<Record<string, number>>({});
  const [fileNamesMap, setFileNamesMap] = useState<Record<string, string[]>>({});
  const [formValues, setFormValues] = useState<Record<string, any>>({});

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [response, setResponse] = useState<ClientTrackResponse | null>(null);
  const fetchClientTrack = async () => {
    const trackUrl = window.location.href;
    const res = await ClientTrackApi.getClientTrack(trackUrl);
    if (res) setResponse(res);
  };
  useEffect(() => {
    fetchClientTrack();
  }, []);

  // ➊ “API” çağrısı (örnek veri)
  useEffect(() => {
    const data: PageConfig = {
      rawFormElements: [
        {
          id: "nameSurname",
          type: "input",
          label: "Adınız ve Soyadınız",
          rowId: 1,
          colClass: "col-6",
          inputType: "text",
          required: true,
        },
        {
          id: "phoneNumber",
          type: "input",
          label: "Telefon Numaranız",
          rowId: 1,
          colClass: "col-6",
          inputType: "tel",
          required: true,
        },
        {
          id: "citizenRequest",
          type: "textarea",
          label: "Talebiniz",
          rowId: 1,
          colClass: "col-12",
          rows: 3,
          required: true,
        },
        {
          id: "fileUploader",
          type: "fileuploader",
          label: "Dosya Yükle",
          rowId: 1,
          colClass: "col-12",
          url: `${GlobalOperations.BaseUrl}/Portal/UploadFile`,
          multi: true,
          allowedTypes: "all",
          maxFile: 10,
          maxSize: 10485760,
        },
      ],
      initialValues: {
        nameSurname: "",
        phoneNumber: "",
        citizenRequest: "",
        fileUploader: [],
      },
      confirmLabel: "Talep Oluştur",
      showCancelButton: false,
      cancelLabel: "",
    };
    setConfig(data);
  }, [response]);

  // ➋ config yüklendiğinde uploader ve formValues map’lerini başlat
  useEffect(() => {
    if (!config) return;
    const fileUploaderIds = config.rawFormElements
      .filter((el) => el.type === ElementType.FILEUPLOADER)
      .map((el) => el.id as string);

    const countsInit = fileUploaderIds.reduce((acc, id) => ({ ...acc, [id]: 0 }), {});
    const namesInit = fileUploaderIds.reduce((acc, id) => ({ ...acc, [id]: [] as string[] }), {});

    setPendingFileCounts(countsInit);
    setFileNamesMap(namesInit);
    setFormValues(config.initialValues);
  }, [config]);

  // ➌ sayfa meta ve layout atamaları (hook hep çağrılıyor; içerde config kontrol ediliyor)
  useEffect(() => {
    if (!response) return;
    setPageTitle(response.clientName || "");
    setPageSummary(response.clientLowLevetlText || "");
    setVideoHelpUrl("");
    setShowHeader(true);
    setShowSidebar(false);
    setShowSummaryOnMobile(false);
    setLayoutHeaderImage(response.clientBannerUrl);
  }, [config]);

  // —————— Erken dönüş artık HOOK’lardan sonra yalnızca JSX için ——————
  if (!config) {
    return <div>Yükleniyor…</div>;
  }

  // ➍ config’den gelenleri ayıkla
  const { rawFormElements, confirmLabel, showCancelButton, cancelLabel } = config;

  // ➎ generic handler fabrikaları
  const handleFilesUploaded = (id: string) => (uploaded: string[]) => {
    setFileNamesMap((prev) => ({ ...prev, [id]: [...prev[id], ...uploaded] }));
    setFormValues((prev) => ({
      ...prev,
      [`fileNames_${id}`]: [...((prev[`fileNames_${id}`] as string[]) || []), ...uploaded],
    }));
  };

  const handleRemoveUploaded = (id: string) => (name: string) => {
    setFileNamesMap((prev) => ({ ...prev, [id]: prev[id].filter((n) => n !== name) }));
    setFormValues((prev) => ({
      ...prev,
      [`fileNames_${id}`]: ((prev[`fileNames_${id}`] as string[]) || []).filter((n) => n !== name),
    }));
  };

  const handlePendingChange = (id: string) => (count: number) => {
    setPendingFileCounts((prev) => ({ ...prev, [id]: count }));
  };

  const handleChange = (id: string) => (e: Date | null | React.ChangeEvent<any>) => {
    if (e instanceof Date || e === null) {
      setFormValues((prev) => ({ ...prev, [id]: e }));
    } else if (e && typeof e === "object" && "target" in e) {
      const { type, checked, value } = e.target as HTMLInputElement;
      setFormValues((prev) => ({
        ...prev,
        [id]: type === "checkbox" ? checked : value,
      }));
    } else {
      setFormValues((prev) => ({ ...prev, [id]: e }));
    }
  };

  // ➏ her elemana handler’ları iliştir
  const formElements = rawFormElements.map((el) => {
    if (el.type === ElementType.FILEUPLOADER) {
      return {
        ...el,
        onUploadComplete: handleFilesUploaded(el.id),
        onRemoveUploaded: handleRemoveUploaded(el.id),
        onPendingChange: handlePendingChange(el.id),
        id: el.id,
        type: el.type,
        url: el.url,
        multi: el.multi,
        allowedTypes: el.allowedTypes,
        maxFile: el.maxFile,
        maxSize: el.maxSize,
        rowId: el.rowId,
        colClass: el.colClass,
      };
    }
    return {
      ...el,
      value: formValues[el.id] ?? "",
      onChange: handleChange(el.id),
      id: el.id,
      type: el.type,
      rowId: el.rowId,
      colClass: el.colClass,
    };
  });

  // ➐ form submit
  const handleSubmit = async (_values: Record<string, any>) => {
    if (!response) {
      toast.error("Form gönderilemedi: Sayfa bilgileri eksik.");
      return;
    }

    const requestPayload: ClientTrackRequest = {
      trackAppUrl: window.location.href,
      nameSurname: formValues["nameSurname"] || "",
      phoneNo: formValues["phoneNumber"] || "",
      requestContent: formValues["citizenRequest"] || "",
      files: formValues["fileNames_fileUploader"] || [], // ← uploader ID'sine dikkat
    };

    try {
      const result = await ClientTrackApi.createClientTrack(requestPayload);
      if (result?.responseCode === 200) {
        toast.success(result?.text || "Talebiniz başarıyla oluşturuldu.");
        setFormValues(config?.initialValues || {});
        setFormSubmitted(true); // Form kartını gizlemek için
        setSuccessMessage(result?.text); // Üst mesajı değiştirmek için
      } else {
        toast.error(result?.text || "Form gönderimi başarısız.");
      }
    } catch (err) {
      console.error("Gönderim hatası:", err);
      toast.error("Sunucu hatası oluştu.");
    }
  };

  return (
    <div className="content-inner container-fluid pb-0">
      <Helmet>
        <title>{response?.clientHeaderText}</title>
      </Helmet>
      <div className="card">
        <div className="card-body">
          <h6>{formSubmitted ? successMessage : response?.welcomeMessage}</h6>
        </div>
      </div>
      {!formSubmitted && (
        <div className="card">
          <div className="card-body">
            <CustomForm
              elements={formElements}
              onSubmit={handleSubmit}
              handleCancel={() => {}}
              showConfirmButton={true}
              showCancelButton={showCancelButton}
              confirmLabel={confirmLabel}
              cancelLabel={cancelLabel}
              innerRowCustomClass=""
              onNotify={onNotify}
            />
          </div>
        </div>
      )}
      {/* Debugging: Form değerlerini göster */}
      {/* <div className="mt-3">
                <h3>Form Değerleri (debug):</h3>
                <pre>{JSON.stringify(formValues, null, 2)}</pre>
            </div> */}
    </div>
  );
};

export default RequestPage;
